import React from 'react';
import { Box, Grid, Typography } from '@mui/material';

import { PageHeader, InfoCard } from '../components';
import ROUTES from '../config/routes';
import THEME from '../constants/theme';
import { SECTION_SPACING } from '../constants';
import SEO from '../components/SEO';
import { seoData } from '../data/seoData';

/**
 * SitemapPage - Internal development page for quick navigation to all pages
 * This is a temporary page for development and testing purposes
 * @returns {React.Element} SitemapPage component
 */
const SitemapPage = () => {
  // Define all pages with their routes and descriptions
  const pages = [
    // Main Pages
    {
      title: 'Home',
      description: 'Main landing page',
      route: ROUTES.HOME
    },
    {
      title: 'Contact',
      description: 'Contact page',
      route: ROUTES.CONTACT
    },
    {
      title: 'Auth',
      description: 'Authentication and registration',
      route: ROUTES.AUTH
    },

    // About Us Section
    {
      title: 'About Us',
      description: 'About Us - Main page',
      route: ROUTES.ABOUT_US.ROOT
    },
    {
      title: 'CSR',
      description: 'About Us - Corporate Social Responsibility',
      route: ROUTES.ABOUT_US.CSR
    },

    // Strategies Section
    {
      title: 'Strategies',
      description: 'Strategies - Main page',
      route: ROUTES.STRATEGIES.ROOT
    },
    {
      title: 'Quantitative Strategies',
      description: 'Strategies - Investment Programs',
      route: ROUTES.STRATEGIES.INVESTMENT_PROGRAMS
    },
    {
      title: 'Trading Business Vision',
      description: 'Strategies - Trading Business',
      route: ROUTES.STRATEGIES.TRADING_BUSINESS
    },
    {
      title: 'Strand Q',
      description: 'Strategies - Strand Q Investment Program',
      route: ROUTES.STRATEGIES.STRAND_Q
    },
    {
      title: 'Merin',
      description: 'Strategies - Merin Investment Program',
      route: ROUTES.STRATEGIES.MERIN
    },
    {
      title: 'Dravon',
      description: 'Strategies - Dravon Investment Program',
      route: ROUTES.STRATEGIES.DRAVON
    },
    {
      title: 'Global Macro',
      description: 'Strategies - Global Macro Investment Program',
      route: ROUTES.STRATEGIES.GLOBAL_MACRO
    },
    {
      title: 'AMC',
      description: 'Strategies - Actively Managed Certificates',
      route: ROUTES.STRATEGIES.AMC
    },

    // How to Join Section
    {
      title: 'How to Join',
      description: 'How to Join - Main page',
      route: ROUTES.HOW_TO_JOIN.ROOT
    },
    {
      title: 'Internship',
      description: 'How to Join - Internship program',
      route: ROUTES.HOW_TO_JOIN.INTERNSHIP
    },
    {
      title: 'Interview',
      description: 'How to Join - Interview process',
      route: ROUTES.HOW_TO_JOIN.INTERVIEW
    },
    {
      title: 'Preparing for Interview',
      description: 'How to Join - Interview preparation guide',
      route: ROUTES.HOW_TO_JOIN.PREPARING_INTERVIEW
    },
    {
      title: 'UK Recruitment Policy',
      description: 'How to Join - UK recruitment policy',
      route: ROUTES.HOW_TO_JOIN.UK_RECRUITMENT
    },
    {
      title: 'EU Recruitment Policy',
      description: 'How to Join - EU recruitment policy',
      route: ROUTES.HOW_TO_JOIN.EU_RECRUITMENT
    },
    {
      title: 'Job Details (Example)',
      description: 'How to Join - Compliance Analyst London job details',
      route: '/how-to-join/compliance-analyst-london'
    },

    // Library
    {
      title: 'Library',
      description: 'Resources and library',
      route: ROUTES.LIBRARY
    },

    // Legal Section
    {
      title: 'Legal',
      description: 'Legal - Main page',
      route: ROUTES.LEGAL.ROOT
    },
    {
      title: 'Cookie Policy',
      description: 'Legal - Cookie policy',
      route: ROUTES.LEGAL.COOKIE_POLICY
    },
    {
      title: 'Important Disclosures',
      description: 'Legal - Important disclosures',
      route: ROUTES.LEGAL.IMPORTANT_DISCLOSURES
    },
    {
      title: 'Tax Strategy',
      description: 'Legal - Tax strategy',
      route: ROUTES.LEGAL.TAX_STRATEGY
    },
    {
      title: 'Modern Slavery Statement',
      description: 'Legal - Modern slavery statement',
      route: ROUTES.LEGAL.MODERN_SLAVERY
    },
    {
      title: 'Terms and Conditions',
      description: 'Legal - Terms and conditions',
      route: ROUTES.LEGAL.TERMS
    },
    {
      title: 'Recruitment Policy Notice',
      description: 'Legal - Recruitment policy notice',
      route: ROUTES.LEGAL.RECRUITMENT_POLICY
    },
    {
      title: 'Online Privacy Statement',
      description: 'Legal - Online privacy statement',
      route: ROUTES.LEGAL.PRIVACY
    },

    // 404 Page
    {
      title: '404 Not Found',
      description: '404 error page',
      route: '/this-page-does-not-exist'
    }
  ];

  return (
    <>
      <SEO
        title={seoData.sitemap.title}
        content={seoData.sitemap.content}
        keywords={seoData.sitemap.keywords}
        canonical={seoData.sitemap.canonical}
        ogImage={seoData.sitemap.ogImage}
        noindex={seoData.sitemap.noindex}
      />
      <Box sx={{ backgroundColor: THEME.COLORS.NEUTRAL_WHITE }}>
        <PageHeader
          title='Site Navigation Map'
          subtitle='Quick access to all pages for development and testing'
          path='/sitemap'
        />

        <Box
          sx={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: {
              xs: THEME.SPACING.LARGE,
              sm: THEME.SPACING.XL,
              md: SECTION_SPACING
            },
            paddingTop: {
              xs: THEME.SPACING.XL,
              md: SECTION_SPACING
            },
            paddingBottom: {
              xs: THEME.SPACING.XXL,
              md: SECTION_SPACING
            }
          }}
        >
          <Typography
            variant='body1'
            sx={{
              marginBottom: THEME.SPACING.XXL,
              textAlign: 'center',
              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
            }}
          >
            Click on any card to navigate to that page
          </Typography>

          <Grid container spacing={3}>
            {pages.map((page, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <InfoCard
                  title={page.title}
                  description={page.description}
                  linkTo={page.route}
                  variant='elevated'
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default SitemapPage;
